import React, {useState, useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  Segment, Icon, Table, Input, Message, Popup, Accordion,
  Label,
} from 'semantic-ui-react';
import {Actions} from './../../../../../common/actions';

const {getWtsMatriculasHistory} = Actions;

const TruncatedCell = ({content, maxLength = 15}) => {
  if (!content) {
    return <span style={{'color': 'lightgrey'}}>N/A</span>;
  }
  const text = String(content);
  if (text.length <= maxLength) {
    return <span>{text}</span>;
  }

  return (
    <Popup
      content={text}
      trigger={
        <span
          style={{
            'cursor': 'pointer',
            'borderBottom': '1px dashed #ccc',
            'transition': 'color 0.2s ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#2185d0';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'inherit';
          }}
        >
          {`${text.substring(0, maxLength)}...`}
        </span>
      }
    />
  );
};

const DealerSectionHistorico = ({
  dealer, employees, sortColumn, sortDirection, searchQuery, onSort,
}) => {
  const filteredData = useMemo(() => {
    let data = employees.filter(
      (emp) => emp?.ruc && String(emp.ruc).trim() === String(dealer.ruc).trim()
    );
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter((emp) =>
        String(emp.id || '').toLowerCase().includes(q) ||
        String(emp.names || '').toLowerCase().includes(q) ||
        String(emp.last_names || '').toLowerCase().includes(q) ||
        String(emp.document || '').toLowerCase().includes(q) ||
        String(emp.agency_description || emp.agency || '').toLowerCase().includes(q) ||
        String(emp.charge || '').toLowerCase().includes(q) ||
        String(emp.fecha_creacion || '').toLowerCase().includes(q) ||
        String(emp.email || '').toLowerCase().includes(q)
      );
    }
    return data;
  }, [employees, dealer.ruc, searchQuery]);

  const sortedData = useMemo(() => {
    return [...filteredData].sort((a, b) => {
      const valA = String(a[sortColumn] || '').toLowerCase();
      const valB = String(b[sortColumn] || '').toLowerCase();
      if (valA < valB) {
        return sortDirection === 'ascending' ? -1 : 1;
      }
      if (valA > valB) {
        return sortDirection === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortColumn, sortDirection]);

  return (
    <div style={{'marginBottom': '20px'}}>
      <div
        style={{
          'backgroundColor': '#f5f5f5',
          'padding': '10px 15px',
          'borderRadius': '6px',
          'border': '1px solid #d4d4d5',
          'borderLeft': '4px solid #767676',
          'marginBottom': '10px',
          'display': 'flex',
          'justifyContent': 'space-between',
          'alignItems': 'center',
        }}
      >
        <div>
          <strong style={{'color': '#4a4a4a', 'fontSize': '1.1em'}}>
            <Icon name="building" /> {dealer.ruc} - {dealer.nombre}
          </strong>
        </div>
        <div>
          <span
            style={{
              'backgroundColor': '#e8e8e8',
              'color': '#5c5c5c',
              'padding': '5px 10px',
              'borderRadius': '15px',
              'fontWeight': 'bold',
              'border': '1px solid #b5b5b5',
            }}
          >
            Histórico: {filteredData.length}
          </span>
        </div>
      </div>

      <div style={{'overflowX': 'auto'}}>
        <Table
          sortable
          celled
          compact="very"
          size="small"
          textAlign="center"
          style={{'fontSize': '0.75em'}}
        >
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell
                sorted={sortColumn === 'id' ? sortDirection : null}
                onClick={() => onSort('id')}
              >
                Cód
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'document' ? sortDirection : null}
                onClick={() => onSort('document')}
              >
                Cédula
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'names' ? sortDirection : null}
                onClick={() => onSort('names')}
              >
                Nombres
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'last_names' ? sortDirection : null}
                onClick={() => onSort('last_names')}
              >
                Apellidos
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'agency_description' ? sortDirection : null}
                onClick={() => onSort('agency_description')}
              >
                Agencia
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'charge' ? sortDirection : null}
                onClick={() => onSort('charge')}
              >
                Cargo
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'email' ? sortDirection : null}
                onClick={() => onSort('email')}
              >
                Correo
              </Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortColumn === 'fecha_creacion' ? sortDirection : null}
                onClick={() => onSort('fecha_creacion')}
              >
                Fecha Creación
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sortedData.length > 0 ? (
              sortedData.map((emp) => {
                const isInactive = emp.estado_matricula === 'INACTIVO';
                return (
                  <Table.Row
                    key={emp.id}
                    style={{
                      'backgroundColor': isInactive ? '#f9f9f9' : '#ffffff',
                      'color': isInactive ? '#8c8c8c' : 'inherit',
                    }}
                  >
                    <Table.Cell>{emp.id}</Table.Cell>
                    <Table.Cell>{emp.document}</Table.Cell>
                    <Table.Cell textAlign="left">
                      <TruncatedCell content={emp.names} maxLength={12} />
                    </Table.Cell>
                    <Table.Cell textAlign="left">
                      <TruncatedCell content={emp.last_names} maxLength={12} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.agency_description || emp.agency} maxLength={10} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.charge} maxLength={10} />
                    </Table.Cell>
                    <Table.Cell>
                      <TruncatedCell content={emp.email} maxLength={12} />
                    </Table.Cell>
                    <Table.Cell>
                      {emp.fecha_creacion ? String(emp.fecha_creacion).replace('T', ' ') : 'N/A'}
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row>
                <Table.Cell colSpan="8" textAlign="center" style={{'padding': '20px'}}>
                  <span style={{'color': 'grey'}}>
                    {searchQuery ? 'No resultados.' : 'No hay historial para este concesionario.'}
                  </span>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};

const WtsRevisionMatriculaHistorico = ({courseToEdit, reduxData}) => {
  const dispatch = useDispatch();

  const {loading, history} = useSelector((st) => ({
    'loading': st.wtsMatriculas?.loading || false,
    'history': st.wtsMatriculas?.matriculasHistory || [],
  }));

  const [state, setState] = useState({
    'searchQuery': '',
    'sortColumn': 'names',
    'sortDirection': 'ascending',
  });

  const {searchQuery, sortColumn, sortDirection} = state;

  const config = {
    'courseId': courseToEdit?.id,
  };

  const dealersList = useMemo(() => {
    if (!courseToEdit) {
      return [];
    }
    const rawDealers = courseToEdit.concesionarios ||
      courseToEdit.dealerParticipants ||
      courseToEdit.agendamiento_concesionarios || [];

    return rawDealers.map((d) => {
      const officialDealer = (reduxData?.wtsDealers || []).find(
        (wd) => String(wd.ruc).trim() === String(d.ruc).trim()
      );
      return {
        'ruc': d.ruc || '',
        'nombre': officialDealer ? officialDealer.description : (d.description || d.nombre || 'Sin identificar'),
      };
    });
  }, [courseToEdit, reduxData]);

  useEffect(() => {
    if (config.courseId) {
      setState((prev) => ({...prev, 'searchQuery': ''}));
      dispatch(getWtsMatriculasHistory({'id_agenda': config.courseId}));
    }
  }, [config.courseId, dispatch]);

  const updateState = (updates) => setState((prev) => ({...prev, ...updates}));

  const handleSort = (clickedColumn) => {
    if (sortColumn !== clickedColumn) {
      updateState({'sortColumn': clickedColumn, 'sortDirection': 'ascending'});
      return;
    }
    updateState({'sortDirection': sortDirection === 'ascending' ? 'descending' : 'ascending'});
  };

  const [activeIndex, setActiveIndex] = useState([0]);

  const handleAccordionClick = (e, titleProps) => {
    const {index} = titleProps;
    const newIndex = activeIndex.includes(index) ?
      activeIndex.filter((i) => i !== index) :
      [...activeIndex, index];
    setActiveIndex(newIndex);
  };

  return (
    <Segment
      padded="very"
      loading={loading}
      style={{
        'minHeight': '400px', 'display': 'flex', 'flexDirection': 'column',
        'border': 'none', 'boxShadow': 'none', 'padding': '0',
      }}
    >
      <div
        style={{
          'backgroundColor': '#ffffff',
          'padding': '15px 20px',
          'borderRadius': '8px',
          'border': '1px solid #e0e0e0',
          'marginBottom': '20px',
          'display': 'flex',
          'justifyContent': 'space-between',
          'alignItems': 'center',
          'boxShadow': '0 2px 4px rgba(0,0,0,0.02)',
        }}
      >
        <div>
          <div style={{'margin': '0 0 5px 0', 'color': '#333', 'fontWeight': 'bold', 'fontSize': '1em'}}>
            <Icon name="history" style={{'color': '#767676'}} /> Historial de Concesionarios
          </div>
          <div style={{'margin': '0', 'fontWeight': 'normal', 'color': '#555', 'fontSize': '0.9em'}}>
            Total Registros Históricos: <strong>{history.length}</strong>
          </div>
        </div>
        <Input
          icon="search"
          placeholder="Buscar en historial..."
          value={searchQuery}
          onChange={(e, {value}) => updateState({'searchQuery': value})}
          style={{'width': '250px'}}
        />
      </div>

      <div style={{'flex': 1, 'overflowY': 'auto', 'overflowX': 'hidden'}}>
        {dealersList.length > 0 ? (
          <Accordion fluid styled exclusive={false}>
            {dealersList.map((dealer, index) => {
              const currentDealerHistory = history.filter(
                (e) => String(e.ruc).trim() === String(dealer.ruc).trim()
              ).length;

              return (
                <React.Fragment key={dealer.ruc}>
                  <Accordion.Title
                    active={activeIndex.includes(index)}
                    index={index}
                    onClick={handleAccordionClick}
                  >
                    <Icon name="dropdown" />
                    <span style={{'fontWeight': 'bold', 'color': '#4a4a4a'}}>
                      {dealer.ruc} - {dealer.nombre}{' '}
                    </span>
                    <Label
                      circular
                      color="grey"
                      size="mini"
                      style={{'marginLeft': '10px'}}
                    >
                      {currentDealerHistory}
                    </Label>
                  </Accordion.Title>
                  <Accordion.Content active={activeIndex.includes(index)}>
                    <DealerSectionHistorico
                      dealer={dealer}
                      employees={history}
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      searchQuery={searchQuery}
                      onSort={handleSort}
                    />
                  </Accordion.Content>
                </React.Fragment>
              );
            })}
          </Accordion>
        ) : (
          <Message
            warning
            icon="warning sign"
            header="Sin concesionarios"
            content="No hay concesionarios asignados a este curso."
          />
        )}
      </div>
    </Segment>
  );
};

export default WtsRevisionMatriculaHistorico;
